import { ThumbUpAltRounded, ThumbDownAltRounded } from '@mui/icons-material'
import { Typography } from '@mui/material'
import { StudentThankYouScreenContent, FeedbackType, FeedbackTypeMap } from 'utils/constants'
import type { InitialFeedbackScreenProps } from './feedbackTypes'
import {
    FeedbackCard,
    FeedbackOptionsWrapper,
    FeedbackOptionContainer,
    IconWrapper,
    FeedbackLabel
} from './feedbackStyles'

// ==================== Initial Feedback Screen ====================

export const InitialFeedbackScreen: React.FC<InitialFeedbackScreenProps> = ({ onFeedbackSelect, feedbackOptions }) => {
    const getFeedbackLabel = (feedbackTypeValue: number): string => {
        if (feedbackTypeValue === 1) {
            return StudentThankYouScreenContent.positiveFeedback.label
        }
        return StudentThankYouScreenContent.negativeFeedback.label
    }

    const getFeedbackType = (feedbackTypeValue: number): typeof FeedbackType.POSITIVE | typeof FeedbackType.NEGATIVE => {
        const mapped = FeedbackTypeMap[feedbackTypeValue as keyof typeof FeedbackTypeMap]
        return mapped || FeedbackType.NEGATIVE
    }

    const getIcon = (feedbackType: typeof FeedbackType.POSITIVE | typeof FeedbackType.NEGATIVE): React.ReactNode => {
        return feedbackType === FeedbackType.POSITIVE ? <ThumbUpAltRounded /> : <ThumbDownAltRounded />
    }

    // Get unique feedback types from available options, with positive (1) first
    const uniqueFeedbackTypes = Array.from(new Set(feedbackOptions.map(option => option.feedbackType)))
        .sort((a, b) => b - a)

    const handleKeyDown = (event: React.KeyboardEvent, feedbackType: typeof FeedbackType.POSITIVE | typeof FeedbackType.NEGATIVE): void => {
        // Handle Enter and Space key presses for accessibility
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onFeedbackSelect(feedbackType)
        }
    }

    return (
        <FeedbackCard elevation={0}>
            <Typography variant='subtitle1' color={'text.secondary'}>
                {StudentThankYouScreenContent.question}
            </Typography>
            <FeedbackOptionsWrapper>
                {uniqueFeedbackTypes.map(typeValue => {
                    const feedbackType = getFeedbackType(typeValue)
                    return (
                        <FeedbackOptionContainer
                            key={feedbackType}
                            onClick={() => onFeedbackSelect(feedbackType)}
                            onKeyDown={e => handleKeyDown(e, feedbackType)}
                            role='button'
                            tabIndex={0}
                        >
                            <IconWrapper feedbackType={feedbackType}>
                                {getIcon(feedbackType)}
                            </IconWrapper>
                            <FeedbackLabel variant='subtitle1'>
                                {getFeedbackLabel(typeValue)}
                            </FeedbackLabel>
                        </FeedbackOptionContainer>
                    )
                })}
            </FeedbackOptionsWrapper>
        </FeedbackCard>
    )
}
